import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Activity, Info } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';

interface BatteryCapacitySnapshot {
  id: string;
  property_id: string;
  local_date: string;
  computed_at: string;
  configured_capacity_kwh: number;
  effective_capacity_kwh: number | null;
  capacity_confidence: string;
  variance_pct: number | null;
  delta_from_prior_pct: number | null;
  status: string;
}

interface BatteryHealthSectionProps {
  propertyId: string;
}

export function BatteryHealthSection({ propertyId }: BatteryHealthSectionProps) {
  const [snapshot, setSnapshot] = useState<BatteryCapacitySnapshot | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchLatestSnapshot() {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('battery_capacity_snapshots')
          .select('*')
          .eq('property_id', propertyId)
          .order('local_date', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        setSnapshot(data);
      } catch (err) {
        console.error('Error fetching battery capacity snapshot:', err);
      } finally {
        setLoading(false);
      }
    }

    if (propertyId) {
      fetchLatestSnapshot();
    }
  }, [propertyId]);

  const getConfidenceBadge = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return {
          label: 'High',
          className: 'bg-status-online/20 text-status-online border-status-online/30',
          message: 'Consistent observed capacity',
        };
      case 'medium':
        return {
          label: 'Medium',
          className: 'bg-neon-amber/20 text-neon-amber border-neon-amber/30',
          message: 'Observed behavior is stabilizing',
        };
      case 'low':
      default:
        return {
          label: 'Low',
          className: 'bg-status-stale/20 text-status-stale border-status-stale/30',
          message: 'Learning — limited data',
        };
    }
  };

  const getDeviationDisplay = (effectiveKwh: number | null, configuredKwh: number) => {
    if (effectiveKwh === null) return null;
    const deviation = ((effectiveKwh - configuredKwh) / configuredKwh) * 100;
    const sign = deviation >= 0 ? '+' : '';
    const color = Math.abs(deviation) > 10 
      ? 'text-status-offline' 
      : Math.abs(deviation) > 5 
        ? 'text-status-stale' 
        : 'text-muted-foreground';
    return { value: `${sign}${deviation.toFixed(1)}%`, color };
  };

  if (loading) {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="w-5 h-5 text-neon-cyan" />
            Battery Health & Capacity (Computed)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => (
              <div key={i}>
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!snapshot) {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="w-5 h-5 text-neon-cyan" />
            Battery Health & Capacity (Computed)
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>This estimate is derived from observed charge and discharge behavior and updates automatically as new data is collected.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No capacity data available yet. Capacity will be computed automatically as telemetry is collected.
          </p>
        </CardContent>
      </Card>
    );
  }

  const confidence = getConfidenceBadge(snapshot.capacity_confidence);
  const deviation = getDeviationDisplay(snapshot.effective_capacity_kwh, snapshot.configured_capacity_kwh);

  return (
    <Card className="bg-wt-bg-secondary border-border">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Activity className="w-5 h-5 text-neon-cyan" />
          Battery Health & Capacity (Computed)
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-4 h-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>This estimate is derived from observed charge and discharge behavior and updates automatically as new data is collected.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {/* Effective Capacity - Primary */}
          <div>
            <span className="text-sm text-muted-foreground block">Effective Capacity</span>
            <span className="text-2xl font-mono-numeric font-bold text-neon-cyan">
              {snapshot.effective_capacity_kwh !== null
                ? snapshot.effective_capacity_kwh.toFixed(1)
                : '—'}
              <span className="text-sm text-muted-foreground"> kWh</span>
              {snapshot.effective_capacity_kwh === null && (
                <span className="text-xs text-muted-foreground font-normal block mt-0.5">Learning</span>
              )}
            </span>
          </div>

          {/* Confidence */}
          <div>
            <span className="text-sm text-muted-foreground block">Confidence</span>
            <div className="mt-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className={confidence.className}>
                      {confidence.label}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{confidence.message}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          {/* Configured Capacity - Secondary */}
          <div>
            <span className="text-sm text-muted-foreground block">Configured</span>
            <span className="text-2xl font-mono-numeric font-bold text-muted-foreground">
              {snapshot.configured_capacity_kwh.toFixed(1)} 
              <span className="text-sm">kWh</span>
            </span>
          </div>

          {/* Deviation */}
          <div>
            <span className="text-sm text-muted-foreground block">Deviation</span>
            {deviation ? (
              <span className={`text-2xl font-mono-numeric font-bold ${deviation.color}`}>
                {deviation.value}
              </span>
            ) : (
              <span className="text-2xl font-mono-numeric font-bold text-muted-foreground">—</span>
            )}
          </div>

          {/* Last Evaluated */}
          <div>
            <span className="text-sm text-muted-foreground block">Last Evaluated</span>
            <span className="text-sm font-medium">
              {formatDistanceToNow(new Date(snapshot.computed_at), { addSuffix: true })}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
